/**
 * Shared OEM (Original Equipment Manufacturer) constants
 *
 * Used across:
 * - Zone Setup (ZoneMapTab, ZoneSetupTab)
 * - Tenant Setup (TenantSetupTab)
 * - Any future OEM-related features
 */

/**
 * Every OEM with a US service network, plus 'Other'.
 *
 * This is the roster the rest of the app validates against — most importantly
 * zoneSizeThresholds.ts, which types its per-OEM ladder as Record<OEM, ...> so an
 * addition here that is not given a zone size scale fails the build rather than
 * quietly rendering the wrong size on the zone map.
 *
 * Deliberately excludes exotics (Ferrari, McLaren, Lotus, Aston Martin, Bentley,
 * Lamborghini, Rolls-Royce): negligible recall-service volume, and every entry here
 * shows up in the OEM dropdowns.
 */
export const KNOWN_OEMS = [
  'Ford',
  'Stellantis',
  'General Motors',
  'Toyota',
  'Honda',
  'Nissan',
  'Hyundai',
  'Kia',
  'Volkswagen',
  'BMW',
  'Mercedes-Benz',
  'Audi',
  'Porsche',
  'Volvo',
  'Polestar',
  'Subaru',
  'Mazda',
  'Mitsubishi',
  'Land Rover',
  'Jaguar',
  'Tesla',
  'Rivian',
  'Lucid',
  'VinFast',
  'Other'
] as const;

export type OEM = (typeof KNOWN_OEMS)[number];

/**
 * Default OEM for new zones
 * Most zones are currently Ford, so this provides a sensible default
 */
export const DEFAULT_OEM: OEM = 'Ford';

/**
 * Mapping of OEMs to their vehicle brand names
 * Used for filtering dealer locations by OEM on the Zone Map
 *
 * Note: Brand names should match the `make_name` values in the database
 * (case-insensitive comparison is used)
 *
 * This map has a second job: zoneSizeThresholds.ts derives its OEM aliases from it, so a
 * brand resolves to its parent OEM's zone size ladder (Acura -> Honda, Genesis -> Hyundai,
 * Lexus -> Toyota). Adding a brand here is therefore enough — no parallel list to update.
 */
export const OEM_BRANDS: Partial<Record<OEM, string[]>> = {
  Ford: ['Ford', 'Lincoln'],
  Stellantis: ['Chrysler', 'Dodge', 'Jeep', 'RAM', 'Ram'],
  'General Motors': ['Chevrolet', 'Buick', 'Cadillac', 'GMC'],
  Toyota: ['Toyota', 'Lexus'],
  Honda: ['Honda', 'Acura'],
  Nissan: ['Nissan', 'Infiniti'],
  Hyundai: ['Hyundai', 'Genesis'],
  Kia: ['Kia'],
  Volkswagen: ['Volkswagen', 'VW'],
  BMW: ['BMW', 'Mini'],
  'Mercedes-Benz': ['Mercedes-Benz', 'Mercedes'],
  Audi: ['Audi'],
  Porsche: ['Porsche'],
  Volvo: ['Volvo'],
  Polestar: ['Polestar'],
  Subaru: ['Subaru'],
  Mazda: ['Mazda'],
  Mitsubishi: ['Mitsubishi'],
  'Land Rover': ['Land Rover', 'Range Rover'],
  Jaguar: ['Jaguar'],
  Tesla: ['Tesla'],
  Rivian: ['Rivian'],
  Lucid: ['Lucid'],
  VinFast: ['VinFast']
};

/**
 * Get the list of brand names for an OEM
 * Returns empty array if OEM has no brand mapping (shows all dealers)
 */
export function getBrandsForOEM(oem: string): string[] {
  return OEM_BRANDS[oem as OEM] || [];
}
